"use client"

import { useEffect, useRef, useState } from "react"
import Typed from "typed.js"
import gsap from "@/lib/gsap-init"
import { HardHat, Search } from "lucide-react"
import { Button } from "@/components/ui/button"
import { SearchModal } from "./search-modal"
import logo from "@/public/logo.png"
import Image from "next/image"
import { useTranslations, useLocale } from 'next-intl'
import { Link } from "@/i18n/navigation"
import { 
  Hotel, 
  UtensilsCrossed, 
  Calendar, 
  PartyPopper, 
  MapPin, 
  Camera, 
  Plane, 
  Building2,
  TreePine,
  Car,
  Compass,
  Users,
  Waves,
  Mountain,
  ShoppingBag,
  Music,
  Gamepad2,
  Tent,
  Ship
} from "lucide-react"

export default function HeroSection() {
  const t = useTranslations()
  const locale = useLocale()
  const sectionRef = useRef<HTMLDivElement>(null)
  const videoContainerRef = useRef<HTMLDivElement>(null)
  const titleRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const calqueContainRef = useRef<HTMLDivElement>(null)
  const typedRef = useRef<HTMLSpanElement>(null)
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([])
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0)
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [theme, setTheme] = useState<"light" | "dark">("dark")
  const [searchOpen, setSearchOpen] = useState(false)

  // All services from services page
  const allServices = [
    { icon: Hotel, titleKey: "navigation.hotels", link: "/hotels" },
    { icon: UtensilsCrossed, titleKey: "navigation.restaurants", link: "/restaurants" },
    { icon: Calendar, titleKey: "navigation.events", link: "/events" },
    { icon: PartyPopper, titleKey: "navigation.festivals", link: "/festivals" },
    { icon: MapPin, titleKey: "navigation.destinations", link: "/destinations" },
    { icon: Compass, titleKey: "navigation.activities", link: "/activities" },
    { icon: Building2, titleKey: "navigation.museums", link: "/museums" },
    { icon: Camera, titleKey: "navigation.monuments", link: "/monuments" },
    { icon: Plane, titleKey: "navigation.circuits", link: "/circuits" },
    { icon: TreePine, titleKey: "navigation.states", link: "/states" },
    { icon: Building2, titleKey: "navigation.guestHouses", link: "/guest-houses" },
    { icon: HardHat, titleKey: "navigation.artisanats", link: "/artisanats" },
    
  ]

  // Video URLs
  const videos = [
    { url: "/video1.mp4" },
    { url: "/video2.mp4" },
    { url: "/video3.mp4" },
  ]

  const [displayVideos, setDisplayVideos] = useState(videos)

  /** ─── Initialize video refs array ────────────────────────── **/
  useEffect(() => {
    videoRefs.current = displayVideos.map((_, i) => videoRefs.current[i] || null)
  }, [displayVideos.length])



  /** ─── GSAP Scroll Animations ─────────────────────────────── **/
  useEffect(() => {
    if (!sectionRef.current) return

    const ctx = gsap.context(() => {
      // Video container animation
      gsap.to(videoContainerRef.current, {
        scale: 0.85,
        opacity: 0.7,
        borderRadius: "24px",
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top top",
          end: "bottom center",
          scrub: 1.2,
        },
      })

      gsap.to(calqueContainRef.current, {
        scale: 1,
        opacity: 0.7,
        borderRadius: "24px",
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top top",
          end: "bottom center",
          scrub: 1.2,
        },
      })

      // Title animation - more dynamic
      gsap.to(titleRef.current, {
        y: -100,
        opacity: 0,
        scale: 0.8,
        rotationX: 15,
        transformOrigin: "center top",
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top top",
          end: "center center",
          scrub: 1,
        },
      })

      // Content animation - parallax effect
      gsap.to(contentRef.current, {
        y: -50,
        opacity: 0,
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top+=100 top",
          end: "center center",
          scrub: 1,
        },
      })

      // Text content fade in on load
      gsap.fromTo(
        contentRef.current,
        { opacity: 0, y: 30 },
        {
          opacity: 1,
          y: 0,
          duration: 1.5,
          ease: "power3.out",
          delay: 0.5,
        },
      )

      // Title character animation
      if (titleRef.current) {
        const titleText = titleRef.current.querySelector("h1")
        if (titleText) {
          gsap.fromTo(
            titleText.children,
            { opacity: 0, y: 50 },
            {
              opacity: 1,
              y: 0,
              duration: 1,
              stagger: 0.05,
              ease: "back.out(1.7)",
              delay: 0.2,
            },
          )
        }
      }
    }, sectionRef)

    return () => ctx.revert()
  }, [])

  /** ─── Video Playback Control ─────────────────────────────── **/
  useEffect(() => {
    videoRefs.current.forEach((video, index) => {
      if (video) {
        if (index === currentVideoIndex) {
          video.play().catch(console.error)
        } else {
          video.pause()
          video.currentTime = 0
        }
      }
    })
  }, [currentVideoIndex, displayVideos])

  /** ─── Automatic Background Carousel ───────────────────────── **/
  useEffect(() => {
    if (displayVideos.length <= 1) return

    const interval = setInterval(() => {
      setCurrentVideoIndex((prev) => (prev + 1) % displayVideos.length)
    }, 5000)

    return () => clearInterval(interval)
  }, [displayVideos.length])

  /** ─── Typed.js Animation ─────────────────────────────────── **/
  useEffect(() => {
    const typed = new Typed(typedRef.current!, {
      strings: [
        t('hero.subtitle'),
        t('hero.description'),
        t('hero.title'),
      ],
      typeSpeed: 60,
      backSpeed: 30,
      showCursor: true,
      cursorChar: "|",
      loop: false,
    })

    return () => typed.destroy()
  }, [t])

  const handleVideoEnd = (index: number) => {
    if (index === currentVideoIndex) {
      const video = videoRefs.current[index]
      if (video) {
        video.currentTime = 0
        video.play().catch(console.error)
      }
    }
  }

  const setVideoRef = (index: number) => (el: HTMLVideoElement | null) => {
    videoRefs.current[index] = el
  }

  const handleThemeChange = (newTheme: "light" | "dark") => {
    setTheme(newTheme)
  }

  const scrollToServices = () => {
    const servicesSection = document.getElementById('services')
    if (servicesSection) {
      servicesSection.scrollIntoView({ behavior: 'smooth' })
    }
  }

  return (
    <>
      <SearchModal isOpen={searchOpen} onClose={() => setSearchOpen(false)} />
      <section
        ref={sectionRef}
        className="relative w-full h-screen overflow-hidden flex items-center justify-start bg-background"
        dir={locale === 'ar' ? 'rtl' : 'ltr'}
      >
        {/* ─── Top Navigation Bar ───────────────────────────── */}
        <div className="absolute top-4 sm:top-6 left-0 w-full px-4 sm:px-6 flex items-center justify-between z-30">
          <div className="flex flex-row gap-3 sm:gap-4">
<Image 
  src={logo || "/placeholder.svg"} 
  height="40" 
  width="40" 
  alt="Carthago guide" 
  className="-mt-2 invert brightness-0 sm:h-[50px] sm:w-[50px]" 
/>           <div className="flex flex-row gap-2 sm:gap-3">
              <Search
                className="text-white cursor-pointer hover:text-primary transition-colors w-5 h-5 sm:w-6 sm:h-6"
                onClick={() => setSearchOpen(true)}
              />
            </div>
          </div>

          <div className="flex items-center gap-6">

            {/* ☰ Animated Hamburger Toggle */}
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="text-white p-3 rounded-xl hover:bg-white/20 transition flex flex-col justify-center items-center gap-[6px]"
            >
              {/* Top bar */}
              <span
                className={`block h-[2px] w-7 bg-white transition-all duration-300
                   ${sidebarOpen ? "rotate-45 translate-y-[8px]" : ""}
                `}
              />
              {/* Middle bar */}
              <span
                className={`block h-[2px] w-7 bg-white transition-all duration-300
                   ${sidebarOpen ? "opacity-0" : "opacity-100"}
                `}
              />
              {/* Bottom bar */}
              <span
                className={`block h-[2px] w-7 bg-white transition-all duration-300
                   ${sidebarOpen ? "-rotate-45 -translate-y-[8px]" : ""}
                `}
              />
            </button>
          </div>
        </div>

        {/* Sidebar Panel */}
        <div
          className={`fixed top-0 right-0 h-full w-full z-9999 transform transition-transform duration-300 flex flex-col
          ${sidebarOpen ? "translate-x-0" : "translate-x-full"}`}
          style={{
            background: `linear-gradient(135deg, var(--sidebar) 0%, var(--sidebar) 100%)`,
            backdropFilter: "blur(10px)",
          }}
        >
          {/* Header with Logo and Close Button */}
          <div className="flex items-center justify-between px-6 py-6 border-b border-sidebar-border">
            <button
              onClick={() => setSidebarOpen(false)}
              className="text-sidebar-foreground hover:text-sidebar-primary transition-colors p-2 rounded-lg hover:bg-sidebar-primary/10"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="w-6 h-6"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Menu Items - All Services */}
          <nav className="flex-1 flex flex-col items-center justify-start gap-4 px-6 py-8 overflow-y-auto">
            <div className="grid grid-cols-2 gap-3 w-full max-w-md">
              {allServices.map((service, index) => {
                const IconComponent = service.icon;
                return (
                  <Link
                    key={index}
                    href={service.link}
                    className="flex flex-col items-center gap-2 p-3 rounded-lg hover:bg-sidebar-primary/10 transition-colors group"
                    onClick={() => setSidebarOpen(false)}
                  >
                    <div className="w-8 h-8 rounded-full bg-sidebar-primary/20 flex items-center justify-center group-hover:bg-sidebar-primary/30 transition-colors">
                      <IconComponent className="w-4 h-4 text-sidebar-primary" />
                    </div>
                    <span className="text-xs font-medium text-sidebar-foreground group-hover:text-sidebar-primary transition-colors text-center">
                      {t(service.titleKey)}
                    </span>
                  </Link>
                );
              })}
            </div>
          </nav>

          {/* Social Media Icons - Bottom */}
          <div className="flex justify-center gap-8 px-6 py-8 border-t border-sidebar-border">
            <a href="#" className="text-sidebar-foreground hover:text-sidebar-primary transition-colors">
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm4.64-12.14c-.62-1.45-2-2.43-3.64-2.43s-3.02.98-3.64 2.43c-.36.88-.36 1.85 0 2.73.62 1.45 2 2.43 3.64 2.43s3.02-.98 3.64-2.43c.36-.88.36-1.85 0-2.73zM9 11.75c0-.41.34-.75.75-.75s.75.34.75.75-.34.75-.75.75-.75-.34-.75-.75zm4.5 0c0-.41.34-.75.75-.75s.75.34.75.75-.34.75-.75.75-.75-.34-.75-.75z" />
              </svg>
            </a>
            <a href="#" className="text-sidebar-foreground hover:text-sidebar-primary transition-colors">
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 9h-3v5h-3v-5H8V9h2V7.5c0-1.6.7-2.5 2.4-2.5h1.7v2.2h-1.2c-.8 0-.9.4-.9 1.1V9h2l-.4 3z" />
              </svg>
            </a>
            <a href="#" className="text-sidebar-foreground hover:text-sidebar-primary transition-colors">
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M23 3a10.9 10.9 0 01-3.14 1.53 4.48 4.48 0 00-7.86 3v1A10.66 10.66 0 013 4s-4 9 5 13a11.64 11.64 0 01-7 2s9 5 20 5a9.5 9.5 0 00-9-5.5c4.75 2.25 7-7 7-7s1.5-6.75-3-9z" />
              </svg>
            </a>
            <a href="#" className="text-sidebar-foreground hover:text-sidebar-primary transition-colors">
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M19.615 3.175h-15.23c-2.165 0-3.92 1.755-3.92 3.92v15.23c0 2.165 1.755 3.92 3.92 3.92h15.23c2.165 0 3.92-1.755 3.92-3.92v-15.23c0-2.165-1.755-3.92-3.92-3.92zM12 16.5a4.5 4.5 0 110-9 4.5 4.5 0 010 9zm4.6-8.2a1.05 1.05 0 110-2.1 1.05 1.05 0 010 2.1z" />
              </svg>
            </a>
            <a href="#" className="text-sidebar-foreground hover:text-sidebar-primary transition-colors">
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M19.633 7.997c.013.175.013.349.013.523 0 5.325-4.053 11.461-11.46 11.461-2.282 0-4.402-.661-6.186-1.809.324.037.636.05.973.05a8.07 8.07 0 005.001-1.721 4.036 4.036 0 01-3.767-2.793c.249.037.499.062.761.062.361 0 .724-.05 1.061-.144a4.022 4.022 0 01-3.23-3.953v-.05c.537.299 1.16.486 1.82.511a4.037 4.037 0 01-1.798-3.354c0-.748.199-1.434.548-2.032a11.455 11.455 0 008.331 4.225c-.062-.3-.1-.611-.1-.923a4.006 4.006 0 014.009-3.968 4.025 4.025 0 012.834 1.193 7.974 7.974 0 002.556-.973 4.02 4.02 0 01-1.76 2.216 8.037 8.037 0 002.319-.624 8.645 8.645 0 01-2.019 2.083z" />
              </svg>
            </a>
            <a href="#" className="text-sidebar-foreground hover:text-sidebar-primary transition-colors">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0m3.937 10.957a1.52 1.52 0 00-1.526-1.52h-1.356V6.752h-2.053v2.685H9.386v1.974h1.616v3.653c0 .858.5 2.159 2.126 2.159h2.629v-1.732h-1.889c-.348 0-.527-.308-.527-.811v-3.269h2.416z"
                />
              </svg>
            </a>
          </div>
        </div>

        {/* Background Video Carousel */}
        <div
          ref={videoContainerRef}
          className="absolute inset-0 w-full h-full overflow-hidden"
          style={{ transformOrigin: "center center" }}
        >
          {displayVideos.length > 0 ? (
            displayVideos.map((video, index) => (
              <video
                key={index}
                ref={setVideoRef(index)}
                className="absolute inset-0 w-full h-full object-cover transition-opacity duration-1000"
                style={{
                  opacity: index === currentVideoIndex ? 1 : 0,
                  filter: theme === "light" ? "brightness(0.9) contrast(1.1)" : "brightness(0.8) contrast(1.2)",
                }}
                muted
                loop
                playsInline
                preload="auto"
                onEnded={() => handleVideoEnd(index)}
              >
                <source src={video.url} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            ))
          ) : (
            // Fallback background when no videos match the theme
            <div
              className={`absolute inset-0 w-full h-full ${
                theme === "light"
                  ? "bg-gradient-to-br from-blue-100 to-amber-50"
                  : "bg-gradient-to-br from-gray-900 to-blue-900"
              }`}
            />
          )}
          <div
            ref={calqueContainRef}
            className={`absolute inset-0 ${
              theme === "light"
                ? "bg-gradient-to-b from-black/30 via-black/20 to-black/40"
                : "bg-gradient-to-b from-black/60 via-black/40 to-black/70"
            }`}
          />
        </div>

        {/* Text content */}
        <div ref={contentRef} className={`relative z-10 px-4 sm:px-6 md:px-8 lg:px-12 xl:px-20 max-w-4xl flex flex-col gap-4 sm:gap-6 ${locale === 'ar' ? 'text-right' : 'text-left'}`}>
          <div ref={titleRef}>
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-white drop-shadow-2xl leading-tight">
              <span className="bg-gradient-to-r from-white via-gray-200 to-primary bg-clip-text text-transparent">
                {t('hero.title')}
              </span>
            </h1>
          </div>
          <p className={`text-base sm:text-lg md:text-xl lg:text-2xl text-white font-light drop-shadow-lg max-w-2xl min-h-[4rem] sm:min-h-[5rem] md:min-h-[6rem] flex items-start overflow-hidden ${locale === 'ar' ? 'leading-relaxed' : 'leading-normal'}`}>
            <span ref={typedRef} className="block w-full break-words"></span>
          </p>
          {/* Carousel Controls */}
          {displayVideos.length > 1 && (
            <div className="flex flex-row gap-3 mt-4">
              {displayVideos.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentVideoIndex(index)}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    index === currentVideoIndex
                      ? "bg-white w-6 shadow-lg shadow-white/50"
                      : "bg-white/30 hover:bg-white/60 w-2"
                  }`}
                  aria-label={`Go to video ${index + 1}`}
                />
              ))}
            </div>
          )}
          <div className={`mt-6 sm:mt-8 flex flex-col sm:flex-row gap-3 sm:gap-4`}>
            <Link href="/circuits">
              <Button
                className={`px-6 sm:px-8 py-4 sm:py-6 rounded-lg font-semibold w-48 text-white transition-all hover:cursor-pointer text-sm sm:text-base ${
                  theme === "light"
                    ? "bg-primary hover:bg-primary/90 shadow-lg"
                    : "bg-primary hover:bg-primary/90 shadow-lg shadow-primary/50"
                }`}
              >
                {t('hero.exploreNow')}
              </Button>
            </Link>
            <Button
              variant="outline"
              className="px-6 sm:px-8 py-4 sm:py-6 w-48 rounded-lg font-semibold text-white border-white hover:bg-white/10 transition-all bg-transparent text-sm sm:text-base"
              onClick={scrollToServices}
            >
              {t('common.readMore')}
            </Button>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 animate-bounce">
          <svg className="w-6 h-6 text-white drop-shadow-lg" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
          </svg>
        </div>
      </section>
    </>
  )
}
